import type { ConnectorMultiAgentConfig } from '../services/unifiedWebSocket';
import type { AgentConfiguratorState } from '../types/agentConfigurator';
import type { ConnectorDataset, TrainingJobRequestBody } from '../types/api';
import type { PipelineTask } from '../types/pipeline';
import {
  buildEvaluatorParamsPayload,
  deriveOptimizeByTokenFromSelectionScenario,
  normalizeEvaluationMode,
  parseEvaluatorParamsForm,
  resolveSelectionScenario,
} from '../types/optimizationJob';
import {
  buildFineTuningAgentsFromConfigurator,
  preparePipelineFineTuningTaskConfig,
} from './fineTuningAgentPayload';
import { formatOptimizationDatasetNameForApi, normalizeOptimizationDatasetNames } from './optimizationDatasetName';
import { buildOptimizationPreviewRequest } from './optimizationPreviewRequest';
import { buildOptimizationPolicy, type OptimizationPolicyUiState } from './optimizationPolicy';
import { optimizationPayloadCoordinator, optimizationPayloadWorker } from './pipelineDefaults';
import type { OptimizationApproach } from '../types/optimizationApproach';
import { isMutationsApproach, normalizeOptimizationApproach } from '../types/optimizationApproach';
import { clampHistoryDepth } from './numberInputDraft';

function normalizeConnectorApproach(value: string): OptimizationApproach {
  return normalizeOptimizationApproach(value);
}

export function buildPipelineConfigTuningPreviewPayload(params: {
  integrationId: string;
  task: PipelineTask;
  configFromConfigurator: AgentConfiguratorState;
  optimizationPolicyUi: OptimizationPolicyUiState;
  optimizationIterations: number;
  datasetNames: string[];
}): ConnectorMultiAgentConfig | null {
  const { integrationId, task, configFromConfigurator, optimizationPolicyUi, optimizationIterations, datasetNames } =
    params;
  const dataset_name = formatOptimizationDatasetNameForApi(datasetNames);
  if (!dataset_name) return null;

  const c = task.config;
  const selectionScenario = resolveSelectionScenario(c);
  const optimization_policy = buildOptimizationPolicy(configFromConfigurator, optimizationPolicyUi);
  const approachRaw =
    (typeof c.approach === 'string' && c.approach.trim()) ||
    configFromConfigurator.approach ||
    'MULTISTAGE-PACE';
  const approach = normalizeConnectorApproach(approachRaw);
  const evaluationMode = isMutationsApproach(approach)
    ? 'config-scorer'
    : normalizeEvaluationMode(c.evaluation_mode);
  const evaluatorParams = parseEvaluatorParamsForm(c.evaluator_params);
  const evaluator_params = buildEvaluatorParamsPayload(evaluationMode, evaluatorParams, {
    includeDirection: true,
  });
  const mp = configFromConfigurator.metaPrompts;
  const taskConfig = c.config;
  const iterations =
    taskConfig &&
    typeof taskConfig === 'object' &&
    !Array.isArray(taskConfig) &&
    Number((taskConfig as { iterations?: number }).iterations) >= 1
      ? Number((taskConfig as { iterations?: number }).iterations)
      : optimizationIterations || 4;

  return {
    integration_id: integrationId,
    approach,
    selection_scenario: selectionScenario,
    evaluation_mode: evaluationMode,
    ...(Object.keys(evaluator_params).length > 0 ? { evaluator_params } : {}),
    optimize_by_token: deriveOptimizeByTokenFromSelectionScenario(selectionScenario),
    optimization_policy,
    config: {
      dataset_name,
      iterations,
      approach,
      coordinator: optimizationPayloadCoordinator(configFromConfigurator.coordinator),
      workers: configFromConfigurator.workers.map((worker) => optimizationPayloadWorker(worker)),
      async_orchestrator: configFromConfigurator.async_orchestrator
        ? { ...configFromConfigurator.async_orchestrator }
        : undefined,
      metaPrompts: mp ? { ...mp } : undefined,
      reminder_prompt_position:
        configFromConfigurator.reminder_prompt_position === 'after' ? 'after' : 'before',
    },
  } as ConnectorMultiAgentConfig;
}

export function buildPipelineConfigTuningPreviewRequest(params: {
  integrationId: string;
  task: PipelineTask;
  configFromConfigurator: AgentConfiguratorState;
  optimizationPolicyUi: OptimizationPolicyUiState;
  optimizationIterations: number;
  datasetNames: string[];
}) {
  const payload = buildPipelineConfigTuningPreviewPayload(params);
  if (!payload) return null;
  return buildOptimizationPreviewRequest(payload);
}

export function resolvePipelinePreviewDatasetNames(
  task: PipelineTask,
  selectedNames: string[]
): string[] {
  const fromSelection = normalizeOptimizationDatasetNames(selectedNames);
  if (fromSelection.length > 0) return fromSelection;

  const c = task.config;
  const fromConfig = normalizeOptimizationDatasetNames(c.dataset_name);
  if (fromConfig.length > 0) return fromConfig;

  const nested = c.config;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const fromNested = normalizeOptimizationDatasetNames((nested as { dataset_name?: unknown }).dataset_name);
    if (fromNested.length > 0) return fromNested;
  }

  return [];
}

export function resolveConnectorDatasetRegistryId(row: ConnectorDataset): string | null {
  const record = row as Record<string, unknown>;
  const candidate = row.id ?? record.dataset_id ?? record.datasetId;
  if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return String(candidate);
  return null;
}

/** Map preview picker values to registry dataset ids (supports id, dataset_id, or label fallback). */
export function resolvePipelineFineTuningPreviewDatasetIds(
  datasets: ConnectorDataset[],
  selected: string[]
): string[] {
  const trimmedSelected = selected.map((value) => value.trim()).filter(Boolean);
  if (trimmedSelected.length === 0) return [];

  const knownIds = new Set<string>();
  const labelToId = new Map<string, string>();
  for (const row of datasets) {
    const id = resolveConnectorDatasetRegistryId(row);
    if (!id) continue;
    knownIds.add(id);
    const label = typeof row.label === 'string' ? row.label.trim() : '';
    if (label) labelToId.set(label, id);
  }

  const resolved: string[] = [];
  for (const value of trimmedSelected) {
    if (knownIds.has(value)) {
      resolved.push(value);
      continue;
    }
    const fromLabel = labelToId.get(value);
    if (fromLabel) {
      resolved.push(fromLabel);
      continue;
    }
    resolved.push(value);
  }
  return Array.from(new Set(resolved));
}

export type PipelineFineTuningPreviewBuildResult =
  | { ok: true; body: TrainingJobRequestBody }
  | { ok: false; error: string };

export function buildPipelineFineTuningPreviewBody(params: {
  integrationId: string;
  task: PipelineTask;
  datasetIds: string[];
  configFromConfigurator?: AgentConfiguratorState | null;
}): PipelineFineTuningPreviewBuildResult {
  const { integrationId, task, datasetIds, configFromConfigurator } = params;
  const ids = datasetIds.map((id) => id.trim()).filter(Boolean);
  if (ids.length === 0) {
    return {
      ok: false,
      error: 'Select at least one saved dataset before running fine-tuning preview.',
    };
  }

  const baseConfig = { ...(task.config as Record<string, unknown>) };
  let prepared = preparePipelineFineTuningTaskConfig(baseConfig);
  let agents = Array.isArray(prepared.agents) ? prepared.agents : [];

  if (agents.length === 0 && configFromConfigurator) {
    prepared = preparePipelineFineTuningTaskConfig({
      ...baseConfig,
      agents: buildFineTuningAgentsFromConfigurator(configFromConfigurator),
    });
    agents = Array.isArray(prepared.agents) ? prepared.agents : [];
  }

  if (agents.length === 0) {
    return {
      ok: false,
      error:
        'No trainable agents in the integration configurator. Mark at least one agent as trainable before preview.',
    };
  }

  const modelName = typeof prepared.model_name === 'string' ? prepared.model_name.trim() : '';
  if (!modelName) {
    return { ok: false, error: 'Model name prefix is required before running fine-tuning preview.' };
  }

  return {
    ok: true,
    body: {
      integration_id: integrationId,
      model_name: modelName,
      dataset_ids: ids,
      group_by_base_model: prepared.group_by_base_model === true,
      merge_train_validation: prepared.merge_train_validation !== false,
      limit_history: prepared.limit_history !== false,
      history_depth:
        prepared.limit_history !== false
          ? clampHistoryDepth(prepared.history_depth as number | string | undefined, 20)
          : null,
      agents: agents as TrainingJobRequestBody['agents'],
    },
  };
}
